import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SqfNav from '../../components/SqfNav';
import RichTextEditor from '../../components/RichTextEditor';
import foodSafetyService from '../../services/foodSafetyService';
import sqfService from '../../services/sqfService';
import sqfAiService from '../../services/sqfAiService';
import { useLanguage, t } from '../../context/LanguageContext';

const SHARED_SECTIONS = [
  { key: 'scope_facility',           title: 'Scope and Facility Description',               contentType: 'richtext' },
  { key: 'management_commitment',    title: 'Management Commitment',                         contentType: 'richtext' },
  { key: 'gmp_prerequisite',         title: 'Good Manufacturing Practices (GMP)',             contentType: 'richtext' },
  { key: 'ssop_general',             title: 'Sanitation Standard Operating Procedures',       contentType: 'richtext' },
  { key: 'allergen_control',         title: 'Allergen Control Program',                       contentType: 'richtext' },
  { key: 'supplier_control',         title: 'Supplier Control Program',                       contentType: 'richtext' },
  { key: 'food_defense',             title: 'Food Defense Plan',                              contentType: 'richtext' },
  { key: 'recall_plan',              title: 'Recall and Withdrawal Procedures',               contentType: 'richtext' },
  { key: 'pest_control',             title: 'Pest Control Program',                           contentType: 'richtext' },
  { key: 'employee_training',        title: 'Employee Training Program',                      contentType: 'richtext' },
  { key: 'equipment_list',           title: 'Equipment and Utensils List',                    contentType: 'richtext' },
  { key: 'water_quality',            title: 'Water Quality and Safety',                       contentType: 'richtext' },
  { key: 'waste_management',         title: 'Waste Management',                               contentType: 'richtext' },
  { key: 'environmental_monitoring', title: 'Environmental Monitoring Program',               contentType: 'richtext' },
];

function LinkedPolicyBanner({ section, lang, onUnlink, unlinking }) {
  const policy = section?.LinkedPolicy;
  const activeVersion = policy?.Versions?.find(v => v.id === policy.activeVersionId);
  const content = lang === 'es'
    ? (activeVersion?.contentEs || activeVersion?.content)
    : (activeVersion?.content || activeVersion?.contentEs);
  return (
    <div>
      <div className="alert alert-info border-start border-4 border-info py-2 px-3 mb-3 d-flex align-items-start gap-2">
        <span style={{ fontSize: 18, lineHeight: 1.4 }}>🔗</span>
        <div style={{ flex: 1 }}>
          <div>
            <strong>{t('Linked to SQF Policy:', 'Vinculada a Política SQF:', lang)}</strong>{' '}
            <span>{(lang === 'es' ? policy?.nameEs : null) || policy?.name}</span>
            {activeVersion && <span className="badge bg-success ms-2" style={{ fontSize: 10 }}>v{activeVersion.versionNumber}</span>}
          </div>
          <div className="small text-muted mt-1">
            {t('Content pulled live from the linked policy.', 'Contenido obtenido de la política vinculada.', lang)}
          </div>
        </div>
        <button className="btn btn-outline-warning btn-sm" onClick={onUnlink} disabled={unlinking}>
          {unlinking ? t('Unlinking…', 'Desvinculando…', lang) : `✂️ ${t('Unlink', 'Desvincular', lang)}`}
        </button>
      </div>
      {content ? (
        <div className="ql-editor border rounded p-3" style={{ background: '#fff', minHeight: 200 }}
          dangerouslySetInnerHTML={{ __html: content }} />
      ) : (
        <div className="border rounded p-4 text-center text-muted" style={{ background: '#fafafa' }}>
          {t('The linked policy has no published content yet.', 'La política vinculada no tiene contenido publicado aún.', lang)}
        </div>
      )}
    </div>
  );
}

export default function FoodSafetyLibraryPage() {
  const { lang } = useLanguage();
  const navigate = useNavigate();

  const [sections, setSections]         = useState({});   // sectionKey → section record or null
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  const [activeKey, setActiveKey]       = useState(null);
  const [editContent, setEditContent]   = useState('');
  const [dirty, setDirty]               = useState(false);
  const [saving, setSaving]             = useState(false);
  const [saveMsg, setSaveMsg]           = useState('');

  const [showPolicyPicker, setShowPolicyPicker] = useState(false);
  const [policies, setPolicies]         = useState([]);
  const [policySearch, setPolicySearch] = useState('');
  const [linkSaving, setLinkSaving]     = useState(false);

  const [toast, setToast]               = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const showToast = useCallback((message, variant = 'success') => {
    setToast({ message, variant });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const load = useCallback(async () => {
    try {
      const list = await foodSafetyService.getLibrary();
      const map = {};
      (list || []).forEach(s => { map[s.sectionKey] = s; });
      setSections(map);
    } catch {
      setError(t('Failed to load library.', 'Error al cargar la biblioteca.', lang));
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!activeKey) return;
    setEditContent(sections[activeKey]?.content || '');
    setDirty(false);
    setSaveMsg('');
  }, [activeKey]); // eslint-disable-line

  const handleSave = async () => {
    if (!activeKey) return;
    setSaving(true);
    try {
      const updated = await foodSafetyService.saveLibrarySection(activeKey, editContent);
      setSections(prev => ({ ...prev, [activeKey]: updated }));
      setSaveMsg(t('Saved', 'Guardado', lang));
      setDirty(false);
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (err) {
      showToast(err?.response?.data?.message || t('Save failed.', 'Error al guardar.', lang), 'danger');
    } finally {
      setSaving(false);
    }
  };

  const openPolicyPicker = async () => {
    try {
      const list = await sqfService.getPolicies();
      setPolicies(list || []);
      setPolicySearch('');
      setShowPolicyPicker(true);
    } catch {
      showToast(t('Failed to load policies.', 'Error al cargar políticas.', lang), 'danger');
    }
  };

  const handleLinkPolicy = async (policyId) => {
    setLinkSaving(true);
    try {
      const updated = await foodSafetyService.linkLibrarySection(activeKey, policyId);
      setSections(prev => ({ ...prev, [activeKey]: updated }));
      setShowPolicyPicker(false);
      showToast(t('Section linked to policy.', 'Sección vinculada a la política.', lang));
    } catch (err) {
      showToast(err?.response?.data?.message || t('Link failed.', 'Error al vincular.', lang), 'danger');
    } finally {
      setLinkSaving(false);
    }
  };

  const handleUnlinkPolicy = () => {
    setConfirmModal({
      title: t('Remove Policy Link', 'Eliminar Vínculo de Política', lang),
      message: t('Remove the link? The section will revert to manual editing.',
                 '¿Eliminar el vínculo? La sección volverá a edición manual.', lang),
      confirmLabel: t('Remove Link', 'Eliminar Vínculo', lang),
      variant: 'warning',
      onConfirm: async () => {
        setLinkSaving(true);
        try {
          const updated = await foodSafetyService.unlinkLibrarySection(activeKey);
          setSections(prev => ({ ...prev, [activeKey]: updated }));
          showToast(t('Policy link removed.', 'Vínculo eliminado.', lang));
        } catch (err) {
          showToast(err?.response?.data?.message || t('Unlink failed.', 'Error al desvincular.', lang), 'danger');
        } finally {
          setLinkSaving(false);
        }
      },
    });
  };

  const activeSection = SHARED_SECTIONS.find(s => s.key === activeKey);
  const activeRecord  = sections[activeKey];
  const isLinked      = !!activeRecord?.linkedPolicyId;

  const filledCount = SHARED_SECTIONS.filter(s => sections[s.key]?.content?.trim() || sections[s.key]?.linkedPolicyId).length;
  const pct = Math.round((filledCount / SHARED_SECTIONS.length) * 100);

  const switchSection = (key) => {
    if (!dirty) { setActiveKey(key); return; }
    setConfirmModal({
      title: t('Unsaved Changes', 'Cambios Sin Guardar', lang),
      message: t('You have unsaved changes. Discard and continue?', '¿Descartar los cambios y continuar?', lang),
      confirmLabel: t('Discard', 'Descartar', lang),
      variant: 'warning',
      onConfirm: () => setActiveKey(key),
    });
  };

  return (
    <div>
      <SqfNav />
      <div style={{ display: 'flex', height: 'calc(100vh - 116px)' }}>

        {/* Sidebar */}
        <div style={{ width: 260, minWidth: 260, borderRight: '1px solid #dee2e6', overflowY: 'auto', background: '#f8f9fa', padding: '12px 0' }}>
          <div className="px-3 mb-3">
            <button className="btn btn-link btn-sm p-0 text-muted text-decoration-none mb-2"
              onClick={() => navigate('/sqf/food-safety')}>
              ← {t('Back to Plans', 'Volver a Planes', lang)}
            </button>
            <div className="fw-bold" style={{ fontSize: 13 }}>📚 {t('Shared Section Library', 'Biblioteca Compartida', lang)}</div>
            <div className="text-muted" style={{ fontSize: 11 }}>
              {t('Applies to both FDA and USDA plans', 'Aplica a ambos planes FDA y USDA', lang)}
            </div>
            <div className="mt-2">
              <div className="d-flex justify-content-between" style={{ fontSize: 10, color: '#888' }}>
                <span>{t('Completion', 'Completado', lang)}</span>
                <span>{filledCount}/{SHARED_SECTIONS.length}</span>
              </div>
              <div className="progress mt-1" style={{ height: 4 }}>
                <div className="progress-bar bg-success" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>

          <div className="px-3 py-1" style={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>
            {t('Shared Sections', 'Secciones Compartidas', lang)}
          </div>
          {loading ? (
            <div className="px-3 text-muted small">{t('Loading…', 'Cargando…', lang)}</div>
          ) : (
            SHARED_SECTIONS.map(sec => {
              const rec = sections[sec.key];
              const hasContent = !!rec?.content?.trim() || !!rec?.linkedPolicyId;
              const isActive   = activeKey === sec.key;
              return (
                <button key={sec.key} onClick={() => switchSection(sec.key)} style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '5px 12px 5px 16px', fontSize: 12, border: 'none', cursor: 'pointer',
                  background: isActive ? '#e8f0fe' : 'transparent',
                  color: isActive ? '#1a73e8' : '#333',
                  fontWeight: isActive ? 600 : 400,
                  borderLeft: isActive ? '3px solid #1a73e8' : '3px solid transparent',
                }}>
                  <span style={{ marginRight: 6, fontSize: 10 }}>
                    {rec?.linkedPolicyId ? '🔗' : hasContent ? '✅' : '○'}
                  </span>
                  {sec.title}
                </button>
              );
            })
          )}
        </div>

        {/* Main area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', maxWidth: 900 }}>
          {error && <div className="alert alert-danger">{error}</div>}

          {!activeKey ? (
            <div className="text-center py-5 text-muted">
              <div style={{ fontSize: 48, marginBottom: 16 }}>📚</div>
              <h5>{t('Select a section from the sidebar to begin editing', 'Selecciona una sección del panel lateral para empezar', lang)}</h5>
              <p className="small mt-2">
                {t(
                  'Shared sections — GMP, SSOP, Allergen Control, and others — appear in both your FDA and USDA plans. Edit them once here and they update everywhere.',
                  'Las secciones compartidas — BPM, SSOP, Control de Alérgenos y otras — aparecen en ambos planes FDA y USDA. Edítalas una vez aquí y se actualizan en todos lados.',
                  lang
                )}
              </p>
            </div>
          ) : (
            <>
              {/* Section header */}
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h5 className="fw-bold mb-0">{activeSection?.title}</h5>
                  <span className="badge bg-info text-dark mt-1" style={{ fontSize: 10 }}>
                    📚 {t('Shared Library — applies to both plans', 'Biblioteca Compartida — aplica a ambos planes', lang)}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {isLinked ? (
                    <>
                      <button className="btn btn-outline-warning btn-sm" onClick={handleUnlinkPolicy} disabled={linkSaving}>
                        {linkSaving ? t('Unlinking…', 'Desvinculando…', lang) : `✂️ ${t('Unlink', 'Desvincular', lang)}`}
                      </button>
                      <button className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate(`/sqf/policies/${activeRecord.linkedPolicyId}/edit`)}>
                        ↗ {t('Go to Policy', 'Ver Política', lang)}
                      </button>
                    </>
                  ) : (
                    <>
                      {saveMsg && <span className="text-success small">{saveMsg}</span>}
                      {dirty && <span className="text-warning small">● {t('Unsaved', 'Sin guardar', lang)}</span>}
                      <button className="btn btn-outline-primary btn-sm" onClick={openPolicyPicker} disabled={saving}>
                        🔗 {t('Link to Policy', 'Vincular a Política', lang)}
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !dirty}>
                        {saving ? t('Saving…', 'Guardando…', lang) : t('Save', 'Guardar', lang)}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Editor */}
              {isLinked ? (
                <LinkedPolicyBanner section={activeRecord} lang={lang} onUnlink={handleUnlinkPolicy} unlinking={linkSaving} />
              ) : (
                <RichTextEditor
                  value={editContent}
                  onChange={val => { setEditContent(val); setDirty(true); setSaveMsg(''); }}
                  onImageUpload={async (file) => { const { fileUrl } = await sqfAiService.uploadFileForAi(file); return fileUrl; }}
                  minHeight={400}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Policy picker modal */}
      {showPolicyPicker && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🔗 {t('Link to SQF Policy', 'Vincular a Política SQF', lang)}</h5>
                <button className="btn-close" onClick={() => setShowPolicyPicker(false)} disabled={linkSaving} />
              </div>
              <div className="modal-body">
                <input className="form-control mb-3" autoFocus
                  placeholder={t('Search policies…', 'Buscar políticas…', lang)}
                  value={policySearch} onChange={e => setPolicySearch(e.target.value)} />
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {policies
                    .filter(p => !p.archivedAt)
                    .filter(p => {
                      const q = policySearch.toLowerCase();
                      return !q || p.name?.toLowerCase().includes(q) || p.nameEs?.toLowerCase().includes(q);
                    })
                    .map(p => (
                      <button key={p.id} className="btn btn-light text-start w-100 mb-1 border"
                        style={{ fontSize: 13 }} onClick={() => handleLinkPolicy(p.id)} disabled={linkSaving}>
                        <div className="fw-semibold">{p.name}</div>
                        {p.nameEs && p.nameEs !== p.name && <div className="text-muted small">{p.nameEs}</div>}
                        {p.activeVersionId && <span className="badge bg-success mt-1" style={{ fontSize: 9 }}>{t('Published', 'Publicada', lang)}</span>}
                      </button>
                    ))
                  }
                  {policies.filter(p => !p.archivedAt).length === 0 && (
                    <p className="text-muted text-center py-3">{t('No policies found.', 'No se encontraron políticas.', lang)}</p>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowPolicyPicker(false)} disabled={linkSaving}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content shadow">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-semibold">{confirmModal.title}</h5>
                <button className="btn-close" onClick={() => setConfirmModal(null)} />
              </div>
              <div className="modal-body pt-2 pb-0">{confirmModal.message}</div>
              <div className="modal-footer border-top-0">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setConfirmModal(null)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button className={`btn btn-${confirmModal.variant || 'danger'} btn-sm`}
                  onClick={() => { confirmModal.onConfirm(); setConfirmModal(null); }}>
                  {confirmModal.confirmLabel || t('Confirm', 'Confirmar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`alert alert-${toast.variant} alert-dismissible mb-0`}
          style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, minWidth: 280, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.message}
          <button type="button" className="btn-close" onClick={() => setToast(null)} />
        </div>
      )}
    </div>
  );
}
